import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Flame, Loader2, ArrowLeft } from "lucide-react";
import { AUTH } from "@/constants/testIds";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // Remember preferred role so the "create account" link sends them straight to the right form
  const preferredRole = (() => {
    try {
      const v = localStorage.getItem("alta_preferred_role");
      return v === "creator" || v === "brand" ? v : "brand";
    } catch (_) { return "brand"; }
  })();

  useEffect(() => {
    if (user && user !== false) {
      const dest = user.user_type === "brand" ? "/painel/marca" : user.user_type === "creator" ? "/painel/criador" : "/painel/admin";
      nav(dest, { replace: true });
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      const dest = u.user_type === "brand" ? "/painel/marca" : u.user_type === "creator" ? "/painel/criador" : "/painel/admin";
      nav(dest, { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A0A0A] flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 spotlight-light pointer-events-none" />
      <div className="w-full max-w-md relative">
        <Link to="/" className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#0A0A0A] text-sm mb-6 w-fit">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl warm-gradient flex items-center justify-center glow-red">
            <Flame className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-bold text-2xl tracking-tight">Alta Creators</span>
        </Link>

        <Card className="bg-white border-[#E8E6E1] p-8 shadow-soft-red-lg">
          <h1 className="font-display font-bold text-3xl tracking-tight mb-1">Bem-vindo de volta</h1>
          <p className="text-sm text-[#6B6B6B] mb-7">Entre para acessar seu painel.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[#0A0A0A] font-medium">E-mail</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-white border-[#E8E6E1] focus-visible:ring-[#DC2626] text-[#0A0A0A]"
                data-testid={AUTH.emailInput}
                placeholder="voce@email.com"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password" className="text-[#0A0A0A] font-medium">Senha</Label>
                <Link
                  to="/esqueci-senha"
                  className="text-xs text-[#DC2626] hover:text-[#B91C1C] font-semibold"
                  data-testid="login-forgot-link"
                >
                  Esqueci minha senha
                </Link>
              </div>
              <PasswordInput
                id="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-white border-[#E8E6E1] focus-visible:ring-[#DC2626] text-[#0A0A0A]"
                data-testid={AUTH.passwordInput}
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div data-testid={AUTH.errorMsg} className="text-sm text-[#DC2626] bg-[#FFF1F1] border border-[#FEE2E2] rounded-lg px-3 py-2">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid={AUTH.submitBtn}
              className="w-full bg-[#DC2626] hover:bg-[#B91C1C] glow-red text-white rounded-full py-6"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
            </Button>

            <div className="text-center text-sm text-[#6B6B6B]">
              Não tem conta?{" "}
              <Link to={`/cadastro?role=${preferredRole}`} className="text-[#DC2626] hover:text-[#B91C1C] font-semibold" data-testid={AUTH.switchLink}>
                Cadastre-se grátis
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
